import { useState } from "react";
import { useEditorStore } from "@/store";
import { generateSourceId } from "@/lib/styleUtils";
import { SOURCE_PRESETS } from "@/lib/presets";
import type { MapLibreSourceType } from "@/types/style";

const SOURCE_TYPES: { type: MapLibreSourceType; label: string }[] = [
  { type: "vector", label: "Vector Tiles" },
  { type: "raster", label: "Raster Tiles" },
  { type: "raster-dem", label: "Raster DEM" },
  { type: "geojson", label: "GeoJSON" },
  { type: "image", label: "Image" },
  { type: "video", label: "Video" },
];

interface AddSourceDialogProps {
  onClose: () => void;
}

export function AddSourceDialog({ onClose }: AddSourceDialogProps) {
  const [sourceType, setSourceType] = useState<MapLibreSourceType>("vector");
  const [id, setId] = useState("");
  const [url, setUrl] = useState("");

  const addSource = useEditorStore((s) => s.addSource);
  const setSelectedSourceId = useEditorStore((s) => s.setSelectedSourceId);

  const viewport = useEditorStore((s) => s.viewport);

  const handleAdd = () => {
    const sourceId = id || generateSourceId(sourceType);
    const source: Record<string, unknown> = { type: sourceType };

    // Approximate viewport bounds for image/video coordinate defaults
    const degW = 360 / Math.pow(2, viewport.zoom) / 2;
    const degH = degW * 0.6;
    const lng = viewport.center[0];
    const lat = viewport.center[1];
    const viewportCoords = [
      [lng - degW, lat + degH],
      [lng + degW, lat + degH],
      [lng + degW, lat - degH],
      [lng - degW, lat - degH],
    ];

    if (sourceType === "vector" || sourceType === "raster" || sourceType === "raster-dem") {
      if (url) source.url = url;
    } else if (sourceType === "geojson") {
      source.data = url || { type: "FeatureCollection", features: [] };
    } else if (sourceType === "image") {
      source.url = url || "";
      source.coordinates = viewportCoords;
    } else if (sourceType === "video") {
      source.urls = url ? [url] : [];
      source.coordinates = viewportCoords;
    }

    // For image/video: add source AND raster layer in one style update
    // (two separate updates can fail because diff:true processes them sequentially)
    if (sourceType === "image" || sourceType === "video") {
      const currentStyle = JSON.parse(JSON.stringify(useEditorStore.getState().style));
      if (!currentStyle.sources) currentStyle.sources = {};
      currentStyle.sources[sourceId] = source;
      currentStyle.layers.push({
        id: `${sourceId}-layer`,
        type: "raster",
        source: sourceId,
        paint: { "raster-opacity": 1 },
      });
      useEditorStore.getState().setStyle(currentStyle);
    } else {
      addSource(sourceId, source);
    }

    setSelectedSourceId(sourceId);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30" onClick={onClose}>
      <div
        className="w-96 rounded-radius-lg border border-border-subtle bg-surface-raised px-6 py-5 shadow-lg"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="mb-4 text-sm font-semibold text-text-primary">Add Source</h2>

        <div className="space-y-3">
          <div>
            <label className="mb-1 block text-[10px] font-medium text-text-secondary">Source ID</label>
            <input
              type="text"
              value={id}
              onChange={(e) => setId(e.target.value)}
              placeholder={generateSourceId(sourceType)}
              className="h-7 w-full rounded-radius-sm border border-border-subtle bg-surface-base px-2 text-xs text-text-primary placeholder:text-text-muted focus:border-accent focus:outline-none"
            />
          </div>

          <div>
            <label className="mb-1 block text-[10px] font-medium text-text-secondary">Type</label>
            <select
              value={sourceType}
              onChange={(e) => setSourceType(e.target.value as MapLibreSourceType)}
              className="h-7 w-full rounded-radius-sm border border-border-subtle bg-surface-base px-2 text-xs text-text-primary focus:border-accent focus:outline-none"
            >
              {SOURCE_TYPES.map((st) => (
                <option key={st.type} value={st.type}>{st.label}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="mb-1 block text-[10px] font-medium text-text-secondary">
              {sourceType === "geojson" ? "Data URL" : "TileJSON URL"}
            </label>
            <input
              type="text"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://..."
              className="h-7 w-full rounded-radius-sm border border-border-subtle bg-surface-base px-2 text-xs text-text-primary placeholder:text-text-muted focus:border-accent focus:outline-none"
            />
          </div>
        </div>

        {/* Presets */}
        <div className="mt-4 border-t border-border-subtle pt-3">
          <div className="mb-1.5 text-[9px] font-medium text-text-muted">Quick add from preset</div>
          <div className="space-y-0.5">
            {SOURCE_PRESETS.map((preset) => (
              <button
                key={preset.name}
                type="button"
                onClick={() => {
                  const srcId = preset.name.toLowerCase().replace(/\s+/g, "-");
                  addSource(srcId, preset.config);
                  setSelectedSourceId(srcId);
                  onClose();
                }}
                className="flex w-full items-start gap-2 rounded-radius-sm px-2 py-1.5 text-left transition-colors hover:bg-surface-sunken"
              >
                <span className="shrink-0 rounded-radius-sm bg-surface-sunken px-1 py-0.5 font-mono text-[8px] text-text-muted">
                  {preset.type}
                </span>
                <div className="flex-1">
                  <div className="text-[10px] font-medium text-text-primary">{preset.name}</div>
                  <div className="text-[9px] text-text-muted">{preset.description}</div>
                </div>
              </button>
            ))}
          </div>
        </div>

        <div className="mt-4 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="rounded-radius-sm px-3 py-1.5 text-xs text-text-secondary hover:bg-surface-sunken">
            Cancel
          </button>
          <button type="button" onClick={handleAdd} className="rounded-radius-sm bg-accent px-3 py-1.5 text-xs font-medium text-accent-text hover:bg-accent-hover">
            Add Source
          </button>
        </div>
      </div>
    </div>
  );
}
